/**
 * Patrón de Ruptura Quantum — estadísticas del dashboard.
 *
 * Vive fuera del adapter a propósito: el adapter es solo lectura del motor y
 * este cálculo agregado (ventana, ratios, R medio, PnL estimado) es propio de
 * la presentación Quantum. Criterio de ventana idéntico a SuperTrend Quantum:
 * últimas 50 señales o 90 días, lo que dé más muestra.
 */

const SIGNAL_WINDOW_DAYS = 90
const SIGNAL_WINDOW_COUNT = 50

/**
 * R de una señal cerrada, por jerarquía de nivel alcanzado: TP3 = +3,
 * TP2 = +2, TP1 = +1, SL = −1. Los niveles del motor son tercios del
 * movimiento medido (tp1 = entry ± fullMove/3 … tp3 = entry ± fullMove), así
 * que la jerarquía tp3Hit > tp2Hit > tp1Hit equivale a esos tercios en R.
 * Timeout: el motor reporta pnl en diferencia de precio (no en R) y con su
 * lógica actual lo fija a 0, así que contamos 0 — queda documentado aquí por
 * si el motor llega a dar un pnl en R usable.
 */
function signalR(sig) {
  if (sig.outcome === 'timeout') return 0
  if (sig.outcome === 'loss') return -1
  if (sig.tp3Hit) return 3
  if (sig.tp2Hit) return 2
  if (sig.tp1Hit) return 1
  return 0
}

/**
 * @param {Array} signals  señales PrqSignal (orden cronológico)
 * @param {number|null} budget  budget por trade; el PnL estimado solo se
 *   calcula si es un número > 0.
 */
export function computeStats(signals, budget) {
  const list = Array.isArray(signals) ? signals : []
  if (list.length === 0) {
    return {
      totalSignals: 0, tp1Rate: 0, tp2Rate: 0, tp3Rate: 0, slRate: 0, openRate: 0,
      winrate: 0, avgR: 0, totalR: 0, closedCount: 0, estimatedPnl: null, last10: [],
    }
  }

  // Ventana: últimas 50 señales o 90 días (mismo criterio que STQ).
  const cutoffTime = Date.now() / 1000 - SIGNAL_WINDOW_DAYS * 24 * 3600
  const sortedDesc = [...list].sort((a, b) => (a.time > b.time ? -1 : a.time < b.time ? 1 : 0))
  const windowed = []
  for (const sig of sortedDesc) {
    if (windowed.length >= SIGNAL_WINDOW_COUNT) break
    if (typeof sig.time === 'number' && sig.time < cutoffTime && windowed.length > 0) break
    windowed.push(sig)
  }
  const windowSignals = windowed.reverse() // cronológico de nuevo

  const total = windowSignals.length
  const tp1Count = windowSignals.filter(s => s.tp1Hit).length
  const tp2Count = windowSignals.filter(s => s.tp2Hit).length
  const tp3Count = windowSignals.filter(s => s.tp3Hit).length
  const slCount = windowSignals.filter(s => s.outcome === 'loss').length
  const openCount = windowSignals.filter(s => s.outcome === 'active').length

  const closed = windowSignals.filter(s => s.outcome !== 'active')
  const closedCount = closed.length
  // Winrate sobre cerradas: gana la que alcanzó al menos TP1 (incluye los BE
  // del motor, que cierran 'win' con pnl 0 tras TP1).
  const winCount = closed.filter(s => s.tp1Hit).length

  const rSum = closed.reduce((sum, s) => sum + signalR(s), 0)
  const hasBudget = Number.isFinite(budget) && budget > 0

  return {
    totalSignals: total,
    tp1Rate: total > 0 ? tp1Count / total : 0,
    tp2Rate: total > 0 ? tp2Count / total : 0,
    tp3Rate: total > 0 ? tp3Count / total : 0,
    slRate: total > 0 ? slCount / total : 0,
    openRate: total > 0 ? openCount / total : 0,
    winrate: closedCount > 0 ? winCount / closedCount : 0,
    avgR: closedCount > 0 ? rSum / closedCount : 0,
    totalR: rSum,
    closedCount,
    estimatedPnl: hasBudget && closedCount > 0 ? rSum * budget : null,
    last10: windowSignals.slice(-10),
  }
}
